// REACT
import { useState } from "react";

// MATERIAL UI
import { Alert, Checkbox, Grid, Stack } from "@mui/material";

// COMPONENTS
import Title from "../common/Title";

// STYLES
import StyledButton from "../dashboard/StyledButton";
import { StyledLinkSpan, StyledTextSpan } from "./styled-components/StyledConsentSpan";

// REDUX
import { setApplicationStep } from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import { useAppDispatch } from "src/store/store";

const checkboxSx = {
  padding: "4px 9px 9px 9px",
  "&:not(.Mui-checked)": {
    color: "#000000",
  },
  "&.Mui-checked": {
    color: "#FC4C4C",
  },
};

/**
 * Privacy notice shown to UK clients at the start of identity verification,
 * before any Onfido page. Clicking "I Consent" records both consents against
 * the user (with a server-side timestamp) as evidence, then continues to
 * Onfido. Non-UK clients go straight to Onfido without this page.
 */
export default function OnfidoPrivacyConsent() {
  const [isBiometricsConsentAccepted, setIsBiometricsConsentAccepted] = useState(false);
  const [isOnfidoTermsAccepted, setIsOnfidoTermsAccepted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  const [recordBiometricsConsent, { isLoading: isRecordingConsent }] =
    api.useOnfidoBiometricsConsentMutation();

  const isButtonDisabled =
    !isBiometricsConsentAccepted || !isOnfidoTermsAccepted || isRecordingConsent;

  const handleConsent = async () => {
    setErrorMessage(null);
    try {
      await recordBiometricsConsent({
        onfidoBiometricsConsentPayloadDto: {
          isBiometricsConsentAccepted,
          isOnfidoTermsAccepted,
        },
      }).unwrap();
    } catch (error) {
      // Consent must be recorded server-side as evidence before identity
      // verification can begin, so block progression and let the user retry.
      setErrorMessage(
        "We couldn't record your consent. Please try again, and contact support if the problem persists.",
      );
      return;
    }
    dispatch(setApplicationStep("onfidoVerify"));
  };

  return (
    <>
      <Title title={{ partB: "Privacy Notice and Consent" }} />
      <Grid container spacing={1} mt={4}>
        <Grid item xs={12} paddingBottom={"16px"}>
          <StyledTextSpan>
            Your personal information, including a government-issued identity document and your
            biometric information, will be collected and handled to verify your identity and for
            compliance purposes, in accordance with Caleb & Brown&rsquo;s{" "}
            <StyledLinkSpan href="https://calebandbrown.com/privacy-policy/" target="_blank">
              Privacy Policy
            </StyledLinkSpan>
            .
          </StyledTextSpan>
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Checkbox
              checked={isBiometricsConsentAccepted}
              onChange={(e) => setIsBiometricsConsentAccepted(e.target.checked)}
              sx={checkboxSx}
            />
            <StyledTextSpan>
              I consent to the collection of my biometric information for the above mentioned
              purposes, in accordance with Caleb & Brown&rsquo;s{" "}
              <StyledLinkSpan href="https://calebandbrown.com/privacy-policy/" target="_blank">
                Privacy Policy
              </StyledLinkSpan>
              .
            </StyledTextSpan>
          </Stack>
        </Grid>
        <Grid item xs={12} paddingBottom={"16px"}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Checkbox
              checked={isOnfidoTermsAccepted}
              onChange={(e) => setIsOnfidoTermsAccepted(e.target.checked)}
              sx={checkboxSx}
            />
            <StyledTextSpan>
              We use Onfido, a third-party identity verification provider, to help verify your
              identity. You agree you have (1) read, understood, and accept Onfido{" "}
              <StyledLinkSpan
                href="https://onfido.com/facial-scan-policy-and-release/"
                target="_blank">
                Facial Scan & Voice Recording Policy
              </StyledLinkSpan>{" "}
              and{" "}
              <StyledLinkSpan href="https://onfido.com/terms-of-service/" target="_blank">
                Terms of Service
              </StyledLinkSpan>{" "}
              and (2) grant consent to Onfido to process your biometric information and other
              personal information in accordance with the Policy.
            </StyledTextSpan>
          </Stack>
        </Grid>
        {errorMessage && (
          <Grid item xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        )}
        <Grid item xs={12} paddingBottom={"16px"}>
          <StyledButton
            disabled={isButtonDisabled}
            onClick={() => {
              handleConsent();
            }}>
            {isRecordingConsent ? "Saving..." : "I Consent"}
          </StyledButton>
        </Grid>
      </Grid>
    </>
  );
}
